/**
 * Сводка обучения с сервера для главной и профиля: профиль ученика,
 * карта программы и размер кармана — одним запросом-хуком.
 *
 * Производные числа (сколько пройдено, следующий урок, освоенные навыки)
 * считаются здесь один раз, чтобы экраны не повторяли один и тот же
 * обход карты.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  BKT_MASTERY,
  type CurriculumMapView,
  type ProfileView,
  type SkillNodeView,
} from '@aptile/shared';

import { fetchCurriculumMap } from '../api/curriculum';
import { fetchProfile } from '../api/onboarding';
import { fetchPocketPage } from '../api/vocabulary';
import { useUserStore } from '../store/userStore';

export interface LearningOverview {
  readonly profile: ProfileView;
  readonly map: CurriculumMapView;
  readonly pocketCount: number;
  readonly completed: number;
  readonly total: number;
  readonly mastered: number;
  /** Следующий урок текущей ступени; `null` — ступень пройдена целиком. */
  readonly next: SkillNodeView | null;
}

export type OverviewState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready'; readonly overview: LearningOverview };

function summarize(profile: ProfileView, map: CurriculumMapView, pocketCount: number): LearningOverview {
  const skills = map.levels.flatMap((level) => level.skills);
  const current = map.levels.find((level) => level.level === map.currentLevel);
  return {
    profile,
    map,
    pocketCount,
    completed: skills.filter((skill) => skill.status === 'completed').length,
    total: skills.length,
    mastered: skills.filter((skill) => (skill.mastery ?? 0) >= BKT_MASTERY).length,
    next: current?.skills.find((skill) => skill.status === 'available') ?? null,
  };
}

export function useLearningOverview(): { readonly state: OverviewState; readonly reload: () => void } {
  const userId = useUserStore((state) => state.userId);
  const [state, setState] = useState<OverviewState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (userId.length === 0) return undefined;
    let cancelled = false;
    Promise.all([fetchProfile(userId), fetchCurriculumMap(userId), fetchPocketPage(userId, { limit: 1 }).catch(() => null)])
      .then(([profile, map, pocket]) => {
        // Имя с сервера — в стор: по нему здороваются шапка и главная (и на новом устройстве).
        useUserStore.getState().setUserName(profile.userName);
        if (!cancelled) setState({ kind: 'ready', overview: summarize(profile, map, pocket?.total ?? 0) });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [userId, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return { state, reload };
}
